/* AiTripDetails.js — Updated: show budget amount + currency */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAiTrip } from "../context/AiTripContext";
import sampleTrip from "./data/sampleGoaTrip.json";
import "./css/AiTripDetails.css";
import {
    collection,
    getDocs,
    query,
    where,
    limit,
    orderBy
} from "firebase/firestore";
import SearchBar from "../components/SearchBar";

import { db } from "../firebase";

function clone(obj) {
    try { return JSON.parse(JSON.stringify(obj)); } catch { return obj; }
}

function OverviewSectionRefined({ draft = {}, raw = {} }) {
    let overview = draft.overview || draft.details || draft.description || raw?.overview || null;
    if (overview && typeof overview === "string") {
        try { overview = JSON.parse(overview); } catch (e) { /* ignore */ }
    }
    const destination = overview?.destination_info || overview?.destination || draft.destination || null;
    const history = overview?.historical_facts || overview?.history || draft.history || null;
    const insights = overview?.traveller_insights || overview?.traveler_insights || draft.traveller_insights || null;

    // Render key/value pairs but skip keys handled separately (description/desc)
    function renderKV(obj) {
        if (obj == null) return null;
        if (typeof obj === "string") return <p className="ov-desc">{obj}</p>;
        if (Array.isArray(obj)) return <ul className="ov-list">{obj.map((it, i) => <li key={i}>{it}</li>)}</ul>;
        if (typeof obj === "object") {
            const skipKeys = new Set(["description", "desc", "image", "summary"]);
            return (
                <div className="ov-keygrid">
                    {Object.entries(obj).map(([k, v], i) => {
                        if (skipKeys.has(k.toLowerCase())) return null;
                        if (v == null || (Array.isArray(v) && v.length === 0) || String(v).trim() === "") return null;
                        const label = String(k).replace(/[_\-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                        return (
                            <div className="ov-row" key={i}>
                                <div className="ov-key">{label}</div>
                                <div className="ov-val">
                                    {Array.isArray(v) ? <ul className="ov-list">{v.map((x, j) => <li key={j}>{x}</li>)}</ul> : <span>{String(v)}</span>}
                                </div>
                            </div>
                        );
                    }).filter(Boolean)}
                </div>
            );
        }
        return <span>{String(obj)}</span>;
    }

    const cardVariants = {
        hidden: { opacity: 0, y: 8, scale: 0.995 },
        enter: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.36, ease: "easeOut" } },
        hover: { scale: 1.02, transition: { duration: 0.18 } }
    };

    return (
        <section className="ai-overview elevated" aria-labelledby="overview-heading">
            <div className="ai-overview-inner">
                <div className="ov-header">
                    <h2 id="overview-heading">Overview</h2>
                    <div className="ov-actions">
                        {draft.highlights ? (
                            Array.isArray(draft.highlights) ? (
                                <div className="ov-pills">{draft.highlights.slice(0, 4).map((h, i) => <span key={i} className="highlight-pill">{h}</span>)}</div>
                            ) : <div className="ov-pills"><span className="highlight-pill">{draft.highlights}</span></div>
                        ) : null}
                    </div>
                </div>

                {(!draft.highlights && (draft.summary || raw?.summary)) && <p className="ov-lead">{draft.summary || raw?.summary}</p>}

                <div className="ov-grid">
                    <AnimatePresence>
                        <motion.div className="ov-card" layout initial="hidden" animate="enter" whileHover="hover" variants={cardVariants} key="dest">
                            <header className="ov-card-head">
                                <h3>Destination</h3>
                                <small className="ov-sub">Quick facts & must-knows</small>
                            </header>
                            <div className="ov-card-body">
                                {destination ? (
                                    <>
                                        {destination.image && <div className="ov-thumb" style={{ backgroundImage: `url(${destination.image})` }} aria-hidden="true" />}
                                        {/* show description/desc once as a lead */}
                                        {(destination.description || destination.desc) && <p className="ov-desc">{destination.description || destination.desc}</p>}
                                        {renderKV(destination)}
                                    </>
                                ) : (
                                    <p className="ov-empty">No structured destination info. Add <code>overview.destination_info</code> to the trip JSON.</p>
                                )}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                <div className="ov-grid">
                    <AnimatePresence>
                        <motion.div className="ov-card" layout initial="hidden" animate="enter" whileHover="hover" variants={cardVariants} key="history">
                            <header className="ov-card-head"><h3>History</h3><small className="ov-sub">Context & highlights</small></header>
                            <div className="ov-card-body">
                                {history ? renderKV(history) : <p className="ov-empty">No historical notes. Add <code>overview.historical_facts</code>.</p>}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                <div className="ov-grid">
                    <AnimatePresence>
                        <motion.div className="ov-card" layout initial="hidden" animate="enter" whileHover="hover" variants={cardVariants} key="insights">
                            <header className="ov-card-head"><h3>Traveller tips</h3><small className="ov-sub">Local hints & safety</small></header>
                            <div className="ov-card-body">
                                {insights ? renderKV(insights) : <p className="ov-empty">No traveller insights. Add <code>overview.traveller_insights</code>.</p>}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                <div className="ov-meta-row">
                    {draft.best_time_to_visit && <div className="ov-meta">Best time: <strong>{draft.best_time_to_visit}</strong></div>}
                    {draft.currency && <div className="ov-meta">Currency: <strong>{draft.currency}</strong></div>}
                    {draft.language && <div className="ov-meta">Language: <strong>{draft.language}</strong></div>}
                </div>
            </div>
        </section>
    );
}
function SidebarTripCard({ trip, onClick }) {
    const budget = trip?.budget?.total || trip?.budget?.approx_value;
    const currency = trip?.budget?.currency || "";
    const days = trip?.trip_days || trip?.itinerary?.length;

    return (
        <div className="sidebar-trip-card" onClick={() => onClick(trip)}>
            <div className="sidebar-content">
                <h4>{trip.title}</h4>
                <p className="sidebar-excerpt">{trip.excerpt}</p>

                <div className="sidebar-meta">
                    {budget && (
                        <span>💰 {budget} {currency}</span>
                    )}
                    {days && (
                        <span>📅 {days} days</span>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function AiTripDetailsFullPage() {
    const { aiTrip } = useAiTrip() || {};
    const navigate = useNavigate();
    const raw = (aiTrip && (aiTrip.raw || aiTrip)) || {};
    const [draft, setDraft] = useState(() => clone(aiTrip || sampleTrip || {}));
    const [otherTrips, setOtherTrips] = useState([]);
    useEffect(() => { if (aiTrip) setDraft(clone(aiTrip)); }, [aiTrip]);
    const { slug } = useParams();
    const title = draft.title || draft.name || raw?.title || "AI Trip Plan";
    const summary = draft.summary || draft.excerpt || raw?.summary || raw?.description || "";
    const tags = Array.isArray(draft.tags) ? draft.tags : (draft.tags ? String(draft.tags).split(",").map(t => t.trim()) : []);
    const itinerary = draft.itinerary || draft.itin || draft.plan || raw?.itinerary || raw?.plan || raw?.itin || draft.days || null;
    const [sidebarQuery, setSidebarQuery] = useState("");
    const daysArray = useMemo(() => {
        if (!itinerary) return null;
        if (Array.isArray(itinerary)) return itinerary;
        if (Array.isArray(itinerary.days)) return itinerary.days;
        if (Array.isArray(itinerary.itinerary)) return itinerary.itinerary;
        if (Array.isArray(draft.days)) return draft.days;
        return null;
    }, [itinerary, draft]);

    // ---------- NEW: robust budget parsing to extract amount and currency ----------
    const budgetInfo = useMemo(() => {
        const src = draft.budget || raw?.budget || null;
        if (!src) return null;

        // helper to normalize numeric string -> stringified integer/float nicely
        function niceNumberStr(n) {
            if (n == null || Number.isNaN(Number(n))) return null;
            const num = Number(n);
            return Number.isInteger(num) ? String(num) : String(num);
        }

        // If it's a number, show that number with no currency
        if (typeof src === 'number') {
            return { amount: niceNumberStr(src), currency: null, raw: src };
        }

        // If it's a string, try to pull number and currency (common patterns)
        if (typeof src === 'string') {
            // common patterns: "1500 EUR", "EUR 1500", "1500 (EUR)", "approx 1500", "1500"
            const currencyMatch = src.match(/([A-Z]{2,3}|€|USD|EUR|GBP|INR)/i);
            const numberMatch = src.match(/([0-9]+(?:[.,][0-9]+)?)/);
            const amount = numberMatch ? niceNumberStr(numberMatch[0].replace(',', '.')) : null;
            const currency = currencyMatch ? currencyMatch[0].toUpperCase().replace('€', 'EUR') : null;
            if (amount || currency) return { amount, currency, raw: src };
            return { amount: src, currency: null, raw: src }; // fallback: keep whole string
        }

        // If it's an object, read common keys
        if (typeof src === 'object') {
            const { total, approx_value, currency, notes } = src;
            // prefer total, then approx_value
            const val = total ?? approx_value ?? src.value ?? src.amount ?? null;
            const parsedAmount = (val != null) ? niceNumberStr(val) : null;
            const parsedCurrency = (currency ? String(currency).trim().toUpperCase() : null) || (src.curr ? String(src.curr).toUpperCase() : null);
            return { amount: parsedAmount, currency: parsedCurrency, notes: notes ?? src.notes ?? null, raw: src };
        }

        return null;
    }, [draft, raw]);
    // -------------------------------------------------------------------------------

    const tripDays = useMemo(() => {
        const explicit = draft.trip_days || draft.daysCount || raw?.trip_days || raw?.tripDays || draft.days;
        if (explicit && Number(explicit)) return Number(explicit);
        if (Array.isArray(daysArray)) return daysArray.length;
        return null;
    }, [draft, raw, daysArray]);

    function renderActivityItem(item, idx) {
        if (item == null) return null;
        if (typeof item === 'string' || typeof item === 'number') return <li key={idx} className="ai-li-simple">{String(item)}</li>;
        if (Array.isArray(item)) return <li key={idx} className="ai-li-nested"><ul>{item.map((inner, j) => renderActivityItem(inner, j))}</ul></li>;
        if (typeof item === 'object') {
            const { time, title: aTitle, name, description, desc, location, note } = item;
            const headline = aTitle || name || item.headline || item.label || null;
            const body = description || desc || note || item.details || null;
            return (
                <li key={idx} className="ai-activity-object">
                    <div className="ai-activity-head">
                        {time && <span className="ai-activity-time">{time}</span>}
                        {headline && <span className="ai-activity-title">{headline}</span>}
                    </div>
                    {location && <div className="ai-activity-location">{location}</div>}
                    {body && <div className="ai-activity-desc">{body}</div>}
                </li>
            );
        }
        return <li key={idx}>{String(item)}</li>;
    }

    function renderDay(day, index) {
        if (typeof day === 'string') return (<article className="ai-day-card" key={index}><p className="ai-day-string">{day}</p></article>);

        const dayTitle = day.title || day.name || day.headline || null;
        const daySummary = day.summary || day.overview || day.description || null;
        const activities = day.activities || day.steps || day.places || day.points || day.tasks || day.items || day.schedule || day.itinerary || null;

        return (
            <article className="ai-day-card" key={index} aria-labelledby={`day-${index}-title`}>
                <header className="ai-day-header">
                    <h3 id={`day-${index}-title`} className="ai-day-title"><span className="ai-day-number">Day {index + 1}</span>{dayTitle && <><span className="ai-day-title-sep">—</span><span className="ai-day-title-text">{dayTitle}</span></>}</h3>
                    {day.duration && <div className="ai-day-meta">{day.duration}</div>}
                </header>

                {daySummary && <p className="ai-day-summary">{daySummary}</p>}

                {activities ? (
                    <ul className="ai-activities">{Array.isArray(activities) ? activities.map((a, i) => renderActivityItem(a, i)) : renderActivityItem(activities, 0)}</ul>
                ) : null}

                {day.notes && <p className="ai-day-notes">{day.notes}</p>}
            </article>
        );
    }

    const filteredTrips = useMemo(() => {
        if (!sidebarQuery.trim()) return otherTrips;

        const q = sidebarQuery.toLowerCase();

        return otherTrips.filter((trip) =>
            [trip.title, trip.excerpt, trip.slug]
                .filter(Boolean)
                .some((field) =>
                    field.toLowerCase().includes(q)
                )
        );
    }, [sidebarQuery, otherTrips]);

    const currentDaysCount = (daysArray && daysArray.length) || 0;
    useEffect(() => {
        async function loadTrips() {
            try {
                const q = query(
                    collection(db, "ai_trips"),
                    where("visibility", "==", "public"),
                    orderBy("createdAt", "desc"),
                    limit(6)
                );

                const snap = await getDocs(q);

                const trips = snap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                }));

                console.log("Sidebar fetched trips:", trips);

                const filtered = trips.filter(
                    t => t.slug && t.slug !== draft.slug
                );

                setOtherTrips(filtered);

            } catch (err) {
                console.error("Sidebar load error:", err);
            }
        }

        loadTrips(); // ← remove draft.slug condition
    }, [draft.slug]);

    useEffect(() => {
        async function loadTripBySlug() {
            try {
                if (!slug) return;

                const q = query(
                    collection(db, "ai_trips"),
                    where("slug", "==", slug),
                    where("visibility", "==", "public"),
                    limit(1)
                );


                const snap = await getDocs(q);

                if (!snap.empty) {
                    const tripData = {
                        id: snap.docs[0].id,
                        ...snap.docs[0].data()
                    };

                    setDraft(clone(tripData));
                } else {
                    console.warn("Trip not found for slug:", slug);
                }

            } catch (err) {
                console.error("Error loading trip:", err);
            }
        }

        loadTripBySlug();
    }, [slug]);


    return (
        <>
            <header className="ai-trip-hero refined-hero">
                <div className="hero-bg-soft" />

                <div className="hero-container">
                    <div className="hero-content">
                        <h1 className="hero-title">{title}</h1>

                        {summary && (
                            <p className="hero-summary">{summary}</p>
                        )}

                        {(budgetInfo || tripDays) && (
                            <div className="hero-meta-row">
                                {budgetInfo && (
                                    <div className="hero-badge">
                                        💰 {budgetInfo.amount || "—"}{" "}
                                        {budgetInfo.currency || ""}
                                    </div>
                                )}

                                {tripDays && (
                                    <div className="hero-badge">
                                        📅 {tripDays} day{tripDays > 1 ? "s" : ""}
                                    </div>
                                )}
                            </div>
                        )}

                        {tags?.length > 0 && (
                            <div className="hero-tags">
                                {tags.slice(0, 6).map((t, i) => (
                                    <span key={i} className="hero-tag">
                                        #{t}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </header>
            <div className="ai-page-card">
                <div className="ai-inner-layout">
                    <section className="ai-main-section">
                        <main className="ai-trip-content" role="main">
                            <div className="ai-back-row">
                                <button
                                    className="ai-back-link"
                                    onClick={() => navigate("/")}
                                >
                                    ← Go Back
                                </button>
                            </div>

                            <OverviewSectionRefined draft={draft} raw={raw} />

                            <section className="ai-itinerary refined-itinerary">
                                <div className="ai-itin-head">
                                    <h2>Suggested itinerary</h2>
                                </div>

                                {daysArray && Array.isArray(daysArray) && daysArray.length > 0 ? (
                                    <div className="ai-days-grid">
                                        {daysArray.map((day, idx) => renderDay(day, idx))}
                                    </div>
                                ) : (
                                    <div className="ai-itin-empty"><p>No detailed itinerary included with this AI suggestion.</p></div>
                                )}
                            </section>

                            {(draft.logistics || draft.packing || draft.map) && (
                                <section className="ai-extras refined-extras">
                                    <h2>Extras</h2>
                                    {draft.logistics && (<div className="ai-extra-block"><h4>Logistics</h4><p>{draft.logistics}</p></div>)}
                                    {draft.packing && (<div className="ai-extra-block"><h4>Packing list</h4>{Array.isArray(draft.packing) ? <ul>{draft.packing.map((p, i) => <li key={i}>{p}</li>)}</ul> : <p>{draft.packing}</p>}</div>)}
                                    {/*
                        Map / Route hidden for now.
                        Previously: {draft.map && (<div className="ai-extra-block"><h4>Map / Route</h4><p>{draft.map}</p></div>)}
                        */}
                                </section>
                            )}

                        </main>
                    </section>


                    <section className="ai-sidebar-section">



                        {/* 🔍 Search */}
                        <div style={{ marginBottom: "18px" }}>
                            <SearchBar
                                users={[]}  // disable user search
                                minChars={1}
                                debounceMs={200}
                            />
                        </div>

                        <div className="sidebar-header">
                            <span className="sidebar-header-icon">✨</span>
                            <h3 className="sidebar-heading">Explore More AI Trips</h3>
                        </div>
                        <div className="sidebar-divider" />

                        {filteredTrips.length > 0 ? (
                            filteredTrips.map((trip) => (
                                <SidebarTripCard
                                    key={trip.id}
                                    trip={trip}
                                    onClick={(selected) => {
                                        navigate(`/ai/${selected.slug}`);
                                    }}
                                />
                            ))
                        ) : (
                            <p className="sidebar-empty">No matching trips found.</p>
                        )}

                    </section>

                </div>
            </div>
            <footer className="ai-trip-footer refined-footer">
                <div className="ai-footer-inner">
                    <div className="ai-footer-meta"><small>Generated by • {'Jift Ai'}</small></div>
                </div>
            </footer>
        </>

    );
}
